import { Link2, Link2Off, ExternalLink } from "lucide-react";
import GlassContainer from "@/shared/components/glasscontainer";
import type IntegrationProvider from "@/features/integrations_provider/models/integrationProvider";

export interface ClientIntegrationCardProps {
  provider: IntegrationProvider;
  isConnected: boolean;
  isHighlighted?: boolean;
  onConnect: (providerId: string) => void;
  onDisconnect: (providerId: string) => void;
}

export function ClientIntegrationCard({
  provider,
  isConnected,
  isHighlighted = false,
  onConnect,
  onDisconnect,
}: ClientIntegrationCardProps) {
  return (
    <GlassContainer
      className={`group relative overflow-hidden flex flex-col h-full hover:shadow-xl transition-all duration-300 ${
        isConnected ? "border-green-500/20" : ""
      } ${isHighlighted ? "ring-2 ring-green-400 ring-offset-2 ring-offset-[#f0f0f3] animate-pulse" : ""}`}
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="absolute top-0 right-0 w-28 h-28 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-accent/5 rounded-full blur-2xl" />
      </div>

      <div className="relative z-10 flex flex-col h-full p-5">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          {provider.icon ? (
            <img
              src={provider.icon}
              alt={provider.name}
              className="w-12 h-12 object-contain"
            />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
              <ExternalLink className="size-6 text-accent/50" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-foreground truncate">
              {provider.name}
            </h3>
            <div className="flex items-center gap-1.5 mt-1">
              <div
                className={`w-1.5 h-1.5 rounded-full ${
                  isConnected ? "bg-green-500" : "bg-gray-400"
                }`}
              />
              <span
                className={`text-[10px] font-bold ${
                  isConnected ? "text-green-600" : "text-gray-500"
                }`}
              >
                {isConnected ? "Conectado" : "Sin conectar"}
              </span>
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-foreground/60 leading-relaxed mb-4 flex-1">
          {provider.description ||
            "Integra tu cuenta para acceder a servicios."}
        </p>

        {/* Action */}
        <div className="mt-auto pt-4 border-t border-white/20">
          {isConnected ? (
            <button
              onClick={() => onDisconnect(provider.id)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-red-500/10 text-red-700 border border-red-500/20 rounded-xl hover:bg-red-500/20 transition-all duration-200"
            >
              <Link2Off className="size-4" />
              Desconectar
            </button>
          ) : (
            <button
              onClick={() => onConnect(provider.id)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-accent/10 text-accent border border-accent/20 rounded-xl hover:bg-accent hover:text-white transition-all duration-200"
            >
              <Link2 className="size-4" />
              Conectar
            </button>
          )}
        </div>
      </div>
    </GlassContainer>
  );
}
